import {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {AppDispatch} from '../../gui-common/redux/store';
import {readSaveMessageFlag, setSaveMessageFlag} from '../../gui-common/redux/action/efe/mqttGrabAction';
import {setSnackbarData} from '../../gui-common/redux/action/snackbarAction';
import {Button, Popup, Surface, Switch, Text, useTheme} from '../../tmwx-design-web/src';

const MqttMessageSaveSwitch = ({systemId}: {systemId: string}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {t} = useTranslation();
    const {padding} = useTheme();
    const [saveMessage, setSaveMessage] = useState<boolean | null>(null);
    const [busy, setBusy] = useState(true);
    const [readError, setReadError] = useState(false);
    const pending = useRef<{abort: () => void} | null>(null);

    const load = useCallback(async () => {
        if (!systemId.trim()) return;
        pending.current?.abort();
        const request = dispatch(readSaveMessageFlag(systemId));
        pending.current = request;
        setBusy(true);
        setReadError(false);
        try {
            const value = await request.unwrap();
            if (pending.current === request) setSaveMessage(value);
        } catch {
            if (pending.current === request) {
                setSaveMessage(null);
                setReadError(true);
                dispatch(setSnackbarData({status: 'danger', text: t('errorOccurred')}));
            }
        } finally {
            if (pending.current === request) {
                pending.current = null;
                setBusy(false);
            }
        }
    }, [dispatch, systemId, t]);

    useEffect(() => {
        setSaveMessage(null);
        void load();
        return () => {
            pending.current?.abort();
            pending.current = null;
        };
    }, [load]);

    const toggle = async () => {
        if (busy || saveMessage === null || pending.current || !systemId.trim()) return;
        const request = dispatch(setSaveMessageFlag({systemId, saveMessage: !saveMessage}));
        pending.current = request;
        setBusy(true);
        try {
            const value = await request.unwrap();
            if (pending.current !== request) return;
            setSaveMessage(value);
            dispatch(setSnackbarData({status: 'success', text: t('actionSuccess')}));
        } catch {
            if (pending.current === request) {
                dispatch(setSnackbarData({status: 'danger', text: t('errorOccurred')}));
            }
        } finally {
            if (pending.current === request) {
                pending.current = null;
                setBusy(false);
            }
        }
    };

    return <Popup type='hover' style={{marginRight: padding}} triggerComponent={
        readError ? <Button iconLeft='refresh' status='neutral' variant='ghost' onPress={() => { void load(); }}>
            {t('refresh')}
        </Button> : <div role='switch' aria-label={t('efe.saveMqttMessages')}
            aria-checked={saveMessage === true} aria-disabled={busy || saveMessage === null} aria-busy={busy}
            tabIndex={busy || saveMessage === null ? -1 : 0}
            onClick={() => { void toggle(); }}
            onKeyDown={event => {
                if (event.key === ' ' || event.key === 'Enter') {
                    event.preventDefault();
                    void toggle();
                }
            }}>
            <Switch isEnabled={saveMessage === true} loading={busy} toggleSwitch={() => {}} />
        </div>
    }>
        <Surface style={{padding}} elevation={2}><Text>{t('efe.saveMqttMessages')}</Text></Surface>
    </Popup>;
};

export default MqttMessageSaveSwitch;
